import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  LinearProgress,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import HelpButton from "../../components/utilities/HelpButton";
import { postNotification } from "../../components/utilities/Notifications";
import { handlePagingAll } from "../../components/api/APIUtilities";
import Login from "../user/Login";
import GameSystemPicker from "../../components/utilities/gamesystempickers/GameSystemPicker";
import GenericMultiSelect from "../../components/utilities/GenericMultiSelect";
import { Formik } from "formik";
import moment from "moment";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import * as Yup from "yup";
import GenericPicker from "../../components/utilities/GenericPicker";
import { globalVariables } from "../../global/GlobalVariables";
import LocationPicker from "../../components/utilities/LocationPicker";
import EventCard from "../../components/layout/EventCard";
import ImageUploader, {
  uploadBas64ToS3,
} from "../../components/utilities/ImageUploader";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { signout } from "../../components/user/BCPAuth";
import { useSignout } from "../../components/user/BCPAuthReducers";
import { getData, postData } from "../../components/utilities/UtilityFunctions";

export default function EventSubmission() {
  let { organizationId } = useParams();
  const authContext = useContext(AuthContext);
  const useSignoutReducer = useSignout();
  const globalVars = globalVariables();

  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [event, setEvent] = useState(null);
  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  const [circuits, setCircuits] = useState([]);
  const [leagueDict, setLeagueDict] = useState({});
  const [selectedCircuits, setSelectedCircuits] = useState([]);
  const [oldestStartDate, setOldestStartDate] = useState(null);
  const [earliestEndDate, setEarliestEndDate] = useState(null);

  const [timeZone, setTimeZone] = useState(null);
  const [locations, setLocations] = useState(null);

  // Keeps track of what step of the wizard the user is on (step 0 is either login/gamesystem picker)
  const [step, setStep] = useState(0);

  const theme = useTheme();

  // If user is already signed in when loading the page, asks the user if they would like to continue or switch user
  useEffect(() => {
    if (authContext?.authState?.isAuthenticated) {
      setStep(5);
    }
    // eslint-disable-next-line
  }, []);

  // Finds newestStartDates and earliestEndDates among selected circuits
  useEffect(() => {
    // New array of circuit objects comprised of only selected circuits
    const filteredCircuits = circuits.filter((circuit) =>
      selectedCircuits.includes(circuit.id)
    );

    let newOldestStartDate;
    let newEarliestEndDate;

    // If the user only has one circuit selected, those are the dates
    if (filteredCircuits.length === 1) {
      newOldestStartDate = filteredCircuits[0].startDate;
      setOldestStartDate(filteredCircuits[0].startDate);

      if (filteredCircuits[0].endDate) {
        newEarliestEndDate = filteredCircuits[0].endDate;
        setEarliestEndDate(filteredCircuits[0].endDate);
      } else {
        newEarliestEndDate = moment("3000-12-31T00:00:00.000Z").toISOString();
        setEarliestEndDate(moment("3000-12-31T00:00:00.000Z").toISOString());
      }
    }

    // Finds the date ranges for oldestStartDate and earliestEndDate. Returns true if there is an overlap of dates, false if they do not
    else if (filteredCircuits.length > 1) {
      //Finds oldestStartDate
      const newestDatesArray = filteredCircuits.map((circuit) =>
        moment(circuit.startDate).toISOString()
      );
      newestDatesArray.sort((a, b) => moment(b).diff(moment(a)));
      newOldestStartDate = newestDatesArray[0];
      setOldestStartDate(newestDatesArray[0]);

      // Finds earliestEndDate
      const earliestEndDatesArr = filteredCircuits.map((circuit) => {
        if (circuit.endDate) {
          return moment(circuit.endDate).toISOString();
        } else {
          return moment("3000-12-31T00:00:00.000Z").toISOString();
        }
      });
      earliestEndDatesArr.sort((a, b) => moment(a).diff(moment(b)));
      newEarliestEndDate = earliestEndDatesArr[0];
      setEarliestEndDate(earliestEndDatesArr[0]);
    } else {
      setOldestStartDate(null);
      setEarliestEndDate(null);
    }

    // Sets error to true if there is no overlap of dates between selected circuits
    if (newOldestStartDate && newEarliestEndDate) {
      if (moment(newOldestStartDate).isAfter(moment(newEarliestEndDate))) {
        setError(true);
      }
      if (moment(newOldestStartDate).isSameOrBefore(newEarliestEndDate)) {
        setError(false);
      }
    }
    // eslint-disable-next-line
  }, [selectedCircuits]);

  // Gets all circuits, gameSystems, saved locations and sets them
  useEffect(() => {
    const go = async () => {
      try {
        if (authContext?.authState?.isAuthenticated) {
          fetchCircuits();
          fetchLocations();
        }
      } catch (err) {
        setLoading(false);
        setProcessing(false);
        console.log(err);
      }
    };

    go();
    // eslint-disable-next-line
  }, [authContext?.authState?.isAuthenticated]);

  // Resets selected circuits if user switches gamesystem
  useEffect(() => {
    setSelectedCircuits([]);
  }, [gameSystem]);

  // Generates user's list of events that fall within the selected circuit date ranges
  const EventList = (props) => {
    const { event, setEvent, startDate, endDate, gameSystem } = props;
    const [events, setEvents] = useState(null);

    useEffect(() => {
      const go = async () => {
        try {
          if (startDate && endDate && moment(startDate).isAfter(endDate)) {
            setEvents([]);
          } else {
            var queryStringParameters = {
              toEvents: true,
              gameType: gameSystem.code,
            };
            if (startDate) {
              queryStringParameters.startDate = startDate;
            }
            if (endDate) {
              queryStringParameters.endDate = endDate;
            }
            var apiEvents = await handlePagingAll(
              "/events",
              queryStringParameters
            );
            setEvents(apiEvents);
          }
        } catch (err) {
          postNotification("error", err);
        }
      };
      go();
      // eslint-disable-next-line
    }, []);

    if (events === null) {
      return (
        <TextField
          label="Event"
          value=""
          variant="outlined"
          fullWidth
          readOnly
          disabled
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CircularProgress size={12} />
              </InputAdornment>
            ),
          }}
        />
      );
    }

    if (events?.length > 0) {
      return (
        <GenericPicker
          label="Event"
          inclAny={false}
          name="event"
          list={events.map((el) => {
            return {
              value: el.id,
              label: el.name,
            };
          })}
          value={event?.id || ""}
          handleChange={(e) => {
            let eventSelected = events?.filter(
              (el) => el.id === e.target.value
            )?.[0];
            setEvent(eventSelected);
          }}
        />
      );
    }

    return (
      <TextField
        label="Event"
        value="No events matching dates"
        variant="outlined"
        fullWidth
        readOnly
        disabled
        margin="normal"
      />
    );
  };

  const updateToken = async (leagueId) => {
    let tokens = await handlePagingAll("/leaguetokens", {
      eventId: event.id,
      leagueId: leagueId,
    });

    let token = undefined;
    if (tokens.length) {
      for (let apiToken of tokens) {
        if (apiToken.status === "active" || apiToken.status === "pending") {
          token = apiToken;
          break;
        }
      }
    }

    if (!token && authContext?.authState?.isAuthenticated) {
      let newToken = {
        userId: authContext?.authState?.identity?.userId,
        email: authContext?.authState?.identity?.email,
        leagueId: leagueId,
        eventId: event.id,
      };
      try {
        token = await postData(`/leaguetokens`, {
          body: newToken,
        });
      } catch (err) {
        postNotification("warning", err);
      }
    }

    let newLeagueDict = { ...leagueDict };
    newLeagueDict[leagueId].token = token || {
      status: "failed",
    };
    setLeagueDict(newLeagueDict);
  };

  const TokenStatus = (props) => {
    const { processing, league } = props;

    if (!league?.token && processing) {
      return <CircularProgress size={12} />;
    }
    if (league?.token) {
      return (
        <Chip
          label={league.token.code || "ERROR"}
          size="small"
          sx={{ width: "100%" }}
          color={
            league.token.status.toLowerCase() === "active"
              ? "success"
              : league.token.status.toLowerCase() === "pending" ||
                  league.token.status.toLowerCase() === "used"
                ? "warning"
                : "error"
          }
        />
      );
    }
    return <></>;
  };

  const fetchLocations = async () => {
    if (locations) {
      return;
    }
    let apiLocations = await handlePagingAll("/locations", {
      userId: authContext?.authState?.identity?.userId,
    });

    if (event?.location?.timeZone) {
      setTimeZone(event?.location?.timeZone);
    }

    apiLocations.sort((a, b) => {
      if (event?.locationId) {
        if (a.id === event.locationId) return 1;
        if (b.id === event.locationId) return -1;
      }
      if (a.name && b.name) {
        let nameA = a.name.toLowerCase();
        let nameB = b.name.toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
      } else if (a.name) {
        return 1;
      } else if (b.name) {
        return -1;
      }
      return 0;
    });

    setLocations(apiLocations);
  };

  const fetchCircuits = async () => {
    setProcessing(true);
    setLoading(true);

    let apiData = await handlePagingAll("/leagues", {
      "expand[]": ["gameSystem"],
      gameStoreId: organizationId,
      active: true,
      startDate: `${moment()
        .subtract(31, "days")
        .format("YYYY-MM-DD")}T00:00:00Z`,
    });

    if (apiData) {
      apiData.sort((a, b) => {
        if (a.startDate && b.startDate) {
          if (a.startDate !== b.startDate) {
            const leagueA = moment(a.startDate);
            const leagueB = moment(b.startDate);
            return leagueB.diff(leagueA);
          }
        }

        if (a.startDate === b.startDate) {
          let nameA = a.name.toLowerCase();
          let nameB = b.name.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
        }

        if (a.startDate) {
          return 1;
        }

        if (b.startDate) {
          return -1;
        }
        return 0;
      });

      setCircuits(apiData);
    }

    var apiGameSystems = {};
    for (let apiLeague of apiData) {
      if (apiLeague.gameSystem) {
        if (!apiGameSystems[apiLeague.gameSystem.id]) {
          apiGameSystems[apiLeague.gameSystemId] = {
            ...apiLeague.gameSystem,
            leagues: [],
          };
        }
        apiGameSystems[apiLeague.gameSystemId].leagues.push(apiLeague);
      }
    }
    apiGameSystems = Object.values(apiGameSystems);
    if (apiGameSystems?.length > 0) {
      apiGameSystems.sort((a, b) => {
        if (a.manufacturerOrderNum === b.manufacturerOrderNum) {
          if (a.manufacturer !== b.manufacturer) {
            var manufacturerA = a.manufacturer.toLowerCase();
            var manufacturerB = b.manufacturer.toLowerCase();
            if (manufacturerA > manufacturerB) return 1;
            if (manufacturerA < manufacturerB) return -1;
          }
          return a.gameSystemOrderNum - b.gameSystemOrderNum;
        }
        return a.manufacturerOrderNum - b.manufacturerOrderNum;
      });
    }
    setGameSystems(apiGameSystems);

    setLoading(false);
    setProcessing(false);
  };

  if (!authContext?.authState?.isAuthenticated) {
    return (
      <>
        <Typography
          variant="h2"
          sx={{
            p: 2,
            textAlign: "center",
            mb: -12,
          }}
        >
          Tournament Organizer Details
        </Typography>

        <Login />
      </>
    );
  }

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}

      <Box width="100%">
        <Container
          maxWidth="lg"
          sx={{
            pt: 2,
            pb: 2,
          }}
        >
          <Grid container spacing={1} sx={{ p: 2 }}>
            <Grid item xs={12}>
              <Typography
                variant="h2"
                sx={{
                  textAlign: "center",
                  width: "100%",
                  pb: 2,
                }}
              >
                {step === 0
                  ? "Circuit Details"
                  : step === 1 || step === 2
                    ? "Event Details"
                    : step === 3 || step === 4
                      ? "Overview"
                      : "Organizer Details"}
              </Typography>
            </Grid>

            {step === 5 && authContext?.authState?.isAuthenticated && (
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h4">
                      You are currently logged in as{" "}
                      {`${authContext?.authState?.identity?.given_name} ${authContext?.authState?.identity?.family_name}`}
                      .
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">
                      Would you like to switch user or continue?
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={async () => {
                        await signout(useSignoutReducer, {
                          redirectUrl: window.location.href,
                        });
                        setStep(0);
                      }}
                    >
                      Switch User
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        setStep(0);
                      }}
                    >
                      Continue
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {step === 0 && gameSystems && (
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <GameSystemPicker
                      name="gameType"
                      value={gameSystem?.code || ""}
                      handleChange={(e) => {
                        if (e.target.value) {
                          gameSystems.forEach((gs) => {
                            if (gs.code === e.target.value) {
                              let listDict = {};
                              if (gs.leagues?.length) {
                                for (let l of gs.leagues) {
                                  listDict[l.id] = {
                                    ...l,
                                    label: l.name,
                                  };
                                }
                              }
                              setLeagueDict(listDict);
                              setGameSystem(gs);
                              return true;
                            }
                          });
                        }
                      }}
                      gameSystems={gameSystems}
                      mode="list"
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {gameSystem && (
              <Grid item xs={12}>
                <Grid container sx={{ gap: 1 }}>
                  {step === 0 &&
                    gameSystem &&
                    circuits.length > 0 &&
                    !processing && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body1">
                            Oldest Circuit Start Date:{" "}
                            {oldestStartDate
                              ? moment(oldestStartDate).format("DD MMMM YYYY")
                              : "-------"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body1">
                            Earliest Circuit End Date:{" "}
                            {earliestEndDate &&
                            earliestEndDate !== "3000-12-31T00:00:00.000Z"
                              ? moment(earliestEndDate).format("DD MMMM YYYY")
                              : earliestEndDate
                                ? "No end date"
                                : "-------"}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          {error && (
                            <Typography variant="body1" color="red">
                              Error: There are no valid, overlapping dates for
                              the selected circuits.
                            </Typography>
                          )}

                          <GenericMultiSelect
                            name="selectedCircuits"
                            label="Circuits"
                            value={selectedCircuits}
                            listDict={leagueDict}
                            setFieldValue={(n, v) => {
                              setSelectedCircuits(v);
                            }}
                            list={gameSystem?.leagues.map((circuit) => {
                              return {
                                value: circuit.id,
                                label: circuit.name,
                              };
                            })}
                          />
                        </Grid>
                      </>
                    )}

                  {step === 0 && gameSystem && processing && (
                    <Grid item xs={12}>
                      <LinearProgress />
                    </Grid>
                  )}

                  {step === 0 &&
                    gameSystem &&
                    circuits.length <= 0 &&
                    !processing && (
                      <Grid item xs={12}>
                        <Typography variant="body1">
                          No circuits found
                        </Typography>
                      </Grid>
                    )}

                  {step === 0 && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        disabled={
                          error || !Boolean(earliestEndDate && oldestStartDate)
                        }
                        // color="warning"
                        variant="contained"
                        onClick={() => setStep(1)}
                      >
                        Next
                      </Button>
                    </Grid>
                  )}

                  {step === 1 && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h4">
                          Please select an existing event or create a new one
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Grid
                          container
                          spacing={1}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Grid item xs={12} md={5}>
                            <Tooltip
                              title={
                                earliestEndDate !== "3000-12-31T00:00:00.000Z"
                                  ? `Your registered events between ${moment(
                                      oldestStartDate
                                    ).format("LLLL")} and ${moment(
                                      earliestEndDate
                                    ).format("LLLL")} for ${gameSystem.name}`
                                  : `Your registered events starting from ${moment(
                                      oldestStartDate
                                    ).format("LLLL")} for ${gameSystem.name}`
                              }
                              placement="top"
                            >
                              <Button
                                fullWidth
                                sx={{
                                  textTransform: "none",
                                }}
                              >
                                <EventList
                                  event={event}
                                  setEvent={setEvent}
                                  startDate={oldestStartDate}
                                  endDate={earliestEndDate}
                                  setStep={setStep}
                                  gameSystem={gameSystem}
                                />
                              </Button>
                            </Tooltip>
                          </Grid>

                          <Grid item xs={12} md={1}>
                            <Typography
                              variant="body1"
                              sx={{ p: 0, m: 0, textAlign: "center" }}
                            >
                              OR
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={5}>
                            <Button
                              onClick={() => {
                                setEvent(null);
                                setStep(2);
                              }}
                              variant="contained"
                              fullWidth
                            >
                              New Event
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>

                      {step === 1 && event && (
                        <Grid item xs={12}>
                          <Tooltip
                            title={
                              selectedCircuits.length > 1
                                ? "Request tokens for your selected event"
                                : "Request token for your selected event"
                            }
                            placement="top"
                          >
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => {
                                setStep(3);
                              }}
                            >
                              {selectedCircuits.length > 1
                                ? "Request tokens"
                                : "Request token"}
                            </Button>
                          </Tooltip>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setStep(0)}
                        >
                          Back
                        </Button>
                      </Grid>
                    </>
                  )}

                  {step === 2 && !event && (
                    <>
                      <Grid item xs={12}>
                        <Formik
                          initialValues={{
                            name: "",
                            location: {},
                            locationId: "",
                            isOnlineEvent: false,
                            eventDate: null,
                            eventEndDate: null,
                            numTickets: "",
                            numberOfRounds: 3,
                            eventType: "Singles Event",
                            doublesEvent: false,
                            teamEvent: false,
                            externalUrl: "",
                            eventSubType: "",
                            eventFormat: "",
                          }}
                          validationSchema={Yup.object().shape({
                            name: Yup.string()
                              .min(3, "Minimum 3 Characters!")
                              .max(255, "Maximum 255 Characters!")
                              .required("Required"),
                            locationId: Yup.string().required("Required"),
                            numberOfRounds: gameSystem?.roundsLogBase2
                              ? Yup.number()
                              : Yup.number().test(
                                  "Number-of-Rounds",
                                  "Invalid number of rounds",
                                  (value, context) => {
                                    if (
                                      !context?.parent?.pairingStyle ||
                                      context.parent.pairingStyle.toLowerCase() ===
                                        "swiss"
                                    ) {
                                      if (
                                        value === "" ||
                                        value === null ||
                                        value === undefined
                                      ) {
                                        return context.createError({
                                          message: "Required",
                                        });
                                      }
                                      if (value <= 0) {
                                        return context.createError({
                                          message:
                                            "The number of rounds must be greater than 0!",
                                        });
                                      }
                                    }
                                    return true;
                                  }
                                ),
                            eventDate: Yup.string()
                              .nullable()
                              .required("Required")
                              .test(
                                "Is valid date?",
                                "Invalid event end date",
                                (value) => {
                                  if (!value || value.trim() === "") {
                                    return false;
                                  }
                                  return moment(
                                    value,
                                    "YYYY-MM-DDTHH:mm",
                                    true
                                  ).isValid();
                                }
                              )
                              .test(
                                "Is valid new start date?",
                                "Start date for new events must be no older than 30 days",
                                (value) => {
                                  return moment(
                                    value,
                                    "YYYY-MM-DDTHH:mm",
                                    true
                                  ).isAfter(moment().subtract(30, "days"));
                                }
                              )
                              .test(
                                "Is valid start date?",
                                `Based on the circuits selected, your start date must be on or after ${
                                  oldestStartDate
                                    ? moment(oldestStartDate).format("LLLL")
                                    : ""
                                }`,
                                (value) => {
                                  return (
                                    !oldestStartDate ||
                                    moment(
                                      value,
                                      "YYYY-MM-DDTHH:mm",
                                      true
                                    ).isSameOrAfter(moment(oldestStartDate))
                                  );
                                }
                              )
                              .test(
                                "Is valid start date?",
                                `Based on the circuits selected, your start date must be before ${
                                  earliestEndDate
                                    ? moment(earliestEndDate).format("LLLL")
                                    : ""
                                }`,
                                (value) => {
                                  return (
                                    !earliestEndDate ||
                                    moment(
                                      value,
                                      "YYYY-MM-DDTHH:mm",
                                      true
                                    ).isBefore(moment(earliestEndDate))
                                  );
                                }
                              ),
                            eventEndDate: Yup.string()
                              .nullable()
                              .required("Required")
                              .test(
                                "Is valid date?",
                                "Invalid event end date",
                                (value) => {
                                  if (!value || value.trim() === "") {
                                    return false;
                                  }
                                  return moment(
                                    value,
                                    "YYYY-MM-DDTHH:mm",
                                    true
                                  ).isValid();
                                }
                              )
                              .test(
                                "Is valid end date?",
                                `Based on the circuits selected, your end date must be after ${
                                  oldestStartDate
                                    ? moment(oldestStartDate).format("LLLL")
                                    : ""
                                }`,
                                (value) => {
                                  return (
                                    !oldestStartDate ||
                                    moment(
                                      value,
                                      "YYYY-MM-DDTHH:mm",
                                      true
                                    ).isAfter(moment(oldestStartDate))
                                  );
                                }
                              )
                              .test(
                                "Is valid end date?",
                                `Based on the circuits selected, your end date must be on or before ${
                                  earliestEndDate
                                    ? moment(earliestEndDate).format("LLLL")
                                    : ""
                                }`,
                                (value) => {
                                  return (
                                    !earliestEndDate ||
                                    moment(
                                      value,
                                      "YYYY-MM-DDTHH:mm",
                                      true
                                    ).isSameOrBefore(moment(earliestEndDate))
                                  );
                                }
                              ),
                          })}
                          enableReinitialize
                          onSubmit={async (values) => {
                            setProcessing(true);
                            try {
                              var sanitizedValues = {
                                ...values,
                                ownerId:
                                  authContext?.authState?.identity?.userId,
                                gameType: gameSystem?.code,
                                gameSystemId: gameSystem?.id,

                                // handle timezones
                                eventDate: timeZone
                                  ? moment
                                      .tz(
                                        values.eventDate,
                                        "YYYY-MM-DDTHH:mm",
                                        timeZone
                                      )
                                      .utc()
                                      .format()
                                  : moment(values.eventDate, "YYYY-MM-DDTHH:mm")
                                      .utc()
                                      .format(),
                                eventEndDate: timeZone
                                  ? moment
                                      .tz(
                                        values.eventEndDate,
                                        "YYYY-MM-DDTHH:mm",
                                        timeZone
                                      )
                                      .utc()
                                      .format()
                                  : moment(
                                      values.eventEndDate,
                                      "YYYY-MM-DDTHH:mm"
                                    )
                                      .utc()
                                      .format(),
                              };

                              delete sanitizedValues.eventType;

                              if (
                                !sanitizedValues.externalUrl ||
                                sanitizedValues.externalUrl === ""
                              ) {
                                delete sanitizedValues.externalUrl;
                              }
                              if (
                                !sanitizedValues.eventSubType ||
                                sanitizedValues.eventSubType === ""
                              ) {
                                delete sanitizedValues.eventSubType;
                              }
                              if (
                                !sanitizedValues.eventFormat ||
                                sanitizedValues.eventFormat === ""
                              ) {
                                delete sanitizedValues.eventFormat;
                              }

                              if (sanitizedValues.eventImage) {
                                // upload image to s3
                                var newFile = await uploadBas64ToS3(
                                  sanitizedValues.eventImage
                                );
                                delete sanitizedValues.eventImage;
                                if (newFile.fileId) {
                                  sanitizedValues.photoFileId = newFile.fileId;
                                }
                              }

                              let apiEvent = await postData("/events", {
                                body: sanitizedValues,
                              });

                              let getNewEvent = await getData(
                                `/events/${apiEvent.id}`
                              );

                              postNotification(
                                "success",
                                "Event successfully created."
                              );

                              setEvent(getNewEvent);
                              setProcessing(false);
                              setStep(3);
                              return;
                            } catch (err) {
                              postNotification("error", err);
                              setProcessing(false);
                            }
                          }}
                        >
                          {({
                            errors,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            touched,
                            values,
                            setFieldValue,
                            setFieldTouched,
                          }) => (
                            <form onSubmit={handleSubmit}>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <ImageUploader
                                    values={values}
                                    label={values.name}
                                    name="eventImage"
                                    setFieldValue={setFieldValue}
                                    fileId={event?.photoFileId}
                                    defaultUrl={event?.photo?.url}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    name="name"
                                    label="Event Name"
                                    value={values.name}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(touched.name && errors.name)}
                                    helperText={touched.name && errors.name}
                                  />
                                </Grid>

                                {globalVars?.eventSubTypes?.list?.length >
                                  0 && (
                                  <Grid item xs={12}>
                                    <GenericPicker
                                      name="eventSubType"
                                      label={
                                        globalVars?.eventSubTypes?.label ||
                                        "Event Sub Type"
                                      }
                                      value={values.eventSubType}
                                      handleChange={handleChange}
                                      list={globalVars?.eventSubTypes?.list || []}
                                      inclAny={false}
                                    />
                                  </Grid>
                                )}

                                {gameSystem?.eventFormats?.list?.length > 0 && (
                                  <Grid item xs={12}>
                                    <GenericPicker
                                      name="eventFormat"
                                      label={
                                        gameSystem?.eventFormats?.label ||
                                        "Format"
                                      }
                                      value={values.eventFormat}
                                      handleChange={handleChange}
                                      list={
                                        gameSystem?.eventFormats?.list || []
                                      }
                                      inclAny={false}
                                    />
                                  </Grid>
                                )}

                                <Grid item xs={12}>
                                  <LocationPicker
                                    locationId={values.locationId}
                                    required
                                    locations={locations}
                                    setLocations={setLocations}
                                    setLocation={(location) => {
                                      setFieldValue("location", location);
                                      setFieldValue("locationId", location?.id);
                                      setTimeZone(location?.timezone);
                                      var newLocations = [...locations];
                                      var found = false;
                                      newLocations.forEach((tempLocation) => {
                                        if (tempLocation.id === location?.id) {
                                          found = true;
                                          tempLocation = {
                                            ...tempLocation,
                                            ...location,
                                          };
                                          return true;
                                        }
                                      });
                                      if (!found) {
                                        newLocations.push(location);
                                      }
                                      setLocations(newLocations);
                                    }}
                                    error={Boolean(
                                      touched.locationId && errors.locationId
                                    )}
                                    helperText={
                                      touched.locationId && errors.locationId
                                    }
                                    setFieldTouched={() =>
                                      setFieldTouched("locationId")
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <FormControlLabel
                                    label={
                                      <Typography className="MuiFormControlLabel-label">
                                        Online Event{" "}
                                        <HelpButton title="Enabling this will still require an event location, however the location is only used to determine the appropriate timezone for the event." />
                                      </Typography>
                                    }
                                    sx={{
                                      ml: 0,
                                      width: "100%",
                                      "& .MuiFormControlLabel-label": {
                                        width: "calc(100% - 58px)",
                                      },
                                    }}
                                    control={
                                      <Switch
                                        color="primary"
                                        checked={values.isOnlineEvent}
                                        onChange={(e) => {
                                          setFieldValue(
                                            "isOnlineEvent",
                                            !values.isOnlineEvent
                                          );
                                        }}
                                      />
                                    }
                                    labelPlacement="start"
                                  />
                                </Grid>

                                <Grid item xs={6}>
                                  <TextField
                                    name="numTickets"
                                    label="Expected Player Attendance"
                                    value={values.numTickets}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.numTickets && errors.numTickets
                                    )}
                                    helperText={
                                      touched.numTickets && errors.numTickets
                                    }
                                    type="number"
                                    inputProps={{
                                      min: 0,
                                    }}
                                  />
                                </Grid>

                                {!gameSystem?.roundsLogBase2 && (
                                  <Grid item xs={6}>
                                    <TextField
                                      name="numberOfRounds"
                                      label="Number of Rounds"
                                      value={values.numberOfRounds}
                                      variant="outlined"
                                      fullWidth
                                      required
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      error={Boolean(
                                        touched.numberOfRounds &&
                                          errors.numberOfRounds
                                      )}
                                      helperText={
                                        touched.numberOfRounds &&
                                        errors.numberOfRounds
                                      }
                                      type="number"
                                      inputProps={{
                                        min: 0,
                                      }}
                                    />
                                  </Grid>
                                )}

                                <Grid item xs={6}>
                                  <TextField
                                    name="eventDate"
                                    label="Start Date/Time"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.eventDate && errors.eventDate
                                    )}
                                    helperText={
                                      touched.eventDate && errors.eventDate
                                    }
                                    type="datetime-local"
                                    defaultValue={values.eventDate}
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    sx={{
                                      colorScheme:
                                        theme?.palette?.mode === "dark"
                                          ? "dark"
                                          : null,
                                    }}
                                  />
                                </Grid>

                                <Grid item xs={6}>
                                  <TextField
                                    name="eventEndDate"
                                    label="End Date/Time"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.eventEndDate &&
                                        errors.eventEndDate
                                    )}
                                    helperText={
                                      touched.eventEndDate &&
                                      errors.eventEndDate
                                    }
                                    type="datetime-local"
                                    defaultValue={values.eventEndDate}
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    sx={{
                                      colorScheme:
                                        theme?.palette?.mode === "dark"
                                          ? "dark"
                                          : null,
                                    }}
                                  />
                                </Grid>

                                {((event &&
                                  event?.gameSystem?.allowsTeamEvent) ||
                                  gameSystem?.allowsTeamEvent) && (
                                  <Grid item xs={12}>
                                    <GenericPicker
                                      name="eventType"
                                      label="Event Type"
                                      value={values.eventType}
                                      handleChange={(e) => {
                                        setFieldValue(
                                          "doublesEvent",
                                          e.target.value === "Doubles Event"
                                        );
                                        setFieldValue(
                                          "teamEvent",
                                          e.target.value === "Teams Event" ||
                                            e.target.value === "Doubles Event"
                                        );
                                        handleChange(e);
                                      }}
                                      list={[
                                        "Singles Event",
                                        "Doubles Event",
                                        "Teams Event",
                                      ]}
                                      inclAny={false}
                                    />
                                  </Grid>
                                )}

                                <Grid item xs={12}>
                                  <TextField
                                    name="externalUrl"
                                    label="External URL"
                                    value={values.externalUrl}
                                    variant="outlined"
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={Boolean(
                                      touched.externalUrl && errors.externalUrl
                                    )}
                                    helperText={
                                      touched.externalUrl && errors.externalUrl
                                    }
                                  />
                                </Grid>

                                <Grid item xs={6}>
                                  <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => {
                                      setStep(1);
                                    }}
                                  >
                                    Back
                                  </Button>
                                </Grid>

                                <Grid item xs={6}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    disabled={
                                      !values.name ||
                                      !values.numTickets ||
                                      !values.eventDate ||
                                      !values.eventEndDate
                                    }
                                  >
                                    Save
                                  </Button>
                                </Grid>
                              </Grid>
                            </form>
                          )}
                        </Formik>
                      </Grid>
                      {processing && <ModalLoadingFull />}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {(step === 3 || step === 4) && event && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom>
                              Request Token(s) for {event.name}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Grid container>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Button
                                  disabled
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <EventCard event={event} />
                                </Button>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography variant="h5" gutterBottom>
                                      Requested tokens:
                                    </Typography>
                                  </Grid>

                                  {selectedCircuits?.length > 0 &&
                                    selectedCircuits.map((leagueId) => {
                                      return (
                                        <Grid item xs={12}>
                                          <Grid container>
                                            <Grid item xs={8}>
                                              {leagueDict[leagueId]?.name}
                                            </Grid>
                                            <Grid key={leagueId} item xs={4}>
                                              <TokenStatus
                                                processing={processing}
                                                event={event}
                                                league={leagueDict[leagueId]}
                                              />
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      );
                                    })}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          {step === 3 && (
                            <>
                              <Grid item xs={6}>
                                <Button
                                  onClick={() => {
                                    setEvent(null);
                                    setStep(1);
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  disabled={processing}
                                >
                                  Back
                                </Button>
                              </Grid>
                              <Grid item xs={6}>
                                <Button
                                  onClick={async () => {
                                    setProcessing(true);
                                    try {
                                      for (let leagueId of selectedCircuits) {
                                        await updateToken(leagueId);
                                      }
                                      setStep(4);
                                    } catch (err) {
                                      postNotification("error", err);
                                    }
                                    setProcessing(false);
                                  }}
                                  variant="contained"
                                  color="success"
                                  fullWidth
                                  disabled={processing}
                                >
                                  Submit
                                </Button>
                              </Grid>
                            </>
                          )}

                          {step === 4 && (
                            <Grid item xs={12}>
                              <Button
                                onClick={() => {
                                  setEvent(null);
                                  setSelectedCircuits([]);
                                  setGameSystem(null);
                                  setStep(0);
                                  setOldestStartDate(null);
                                  setEarliestEndDate(null);
                                }}
                                variant="contained"
                                fullWidth
                              >
                                Restart
                              </Button>
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
